import axios from "axios";
import { toast } from "sonner";

import { clearStoredAuthSession, getStoredAuthToken } from "@/shared/auth/session";
import i18n from "@/shared/config/i18n/client";
import { getFingerprint } from "@/shared/lib/fingerprint";
import { logger } from "@/shared/lib/logger";

import { apiBaseUrl } from "./config";
import { getOrCreateIdempotencyKey } from "./utils";

export const baseUrl = apiBaseUrl;

if (process.env.NODE_ENV !== "production") {
  logger.warn(`API client configured for ${baseUrl}`);
}

export const http = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
  headers: {
    Accept: "application/json",
  },
});

http.interceptors.request.use(
  async (config) => {
    if (typeof window !== "undefined") {
      const method = config.method?.toUpperCase();
      if (method && ["POST", "PUT", "DELETE"].includes(method)) {
        config.headers["Idempotency-Key"] = getOrCreateIdempotencyKey();
      }

      // Attach Authorization: Bearer as a fallback when the HttpOnly auth cookie
      // can't ride along (iOS Safari/Chrome and any browser that blocks
      // third-party cookies). The cookie path still works in browsers that
      // allow it; the backend accepts either.
      if (!config.headers.Authorization) {
        const token = getStoredAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      try {
        const visitorId = await getFingerprint();
        config.headers["X-Fingerprint"] = visitorId;
      } catch {
        // Fingerprint load failed — proceed without it
      }
    }

    return config;
  },
  (error) => {
    if (error.response?.status === 401) {
      logger.warn("Unauthorized request");
      if (typeof window !== "undefined") {
        clearStoredAuthSession();
      }
    }

    return Promise.reject(error);
  },
);

function formatWaitTime(seconds: number): string {
  if (seconds >= 60) {
    const minutes = Math.ceil(seconds / 60);
    return i18n.t("common.rateLimit.minutes", { count: minutes });
  }
  return i18n.t("common.rateLimit.seconds", { count: seconds });
}

http.interceptors.response.use(undefined, (error) => {
  if (axios.isAxiosError(error) && error.response?.status === 429) {
    const retryAfter = Number.parseInt(error.response.headers["retry-after"] || "", 10);
    const seconds = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 60;

    toast.warning(i18n.t("common.rateLimit.message", { time: formatWaitTime(seconds) }), {
      duration: Math.min(seconds * 1000, 10_000),
    });
  }

  if (axios.isAxiosError(error) && error.response?.status === 401) {
    if (typeof window !== "undefined") {
      clearStoredAuthSession();
    }
  }

  return Promise.reject(error);
});
